import React, { useMemo } from 'react'
import MappingSelect from '../../Shared/Components/MappingSelect'
import RequiredFlag from './RequiredFlag'
import { leftSide, rightSide, sideFields, withSideField } from '../attributeDirection'
import Shared from '../../Shared/mappingUi.module.css'
import Style from '../productAttribute.module.css'

/**
 * One saved mapping. Changing either dropdown swaps in the whole field - name, data
 * type, size and required flag - so the row keeps the metadata the save call needs.
 */
const AttributeMappingRow = ({
    row,
    direction,
    leftTitle,
    rightTitle,
    erpFields,
    ecommerceFields,
    isDisabled,
    onChange,
    onRemove
}) => {
    const { left: leftFields, right: rightFields } = useMemo(
        () => sideFields(direction, erpFields, ecommerceFields),
        [direction, erpFields, ecommerceFields]
    )

    const left = leftSide(row, direction)
    const right = rightSide(row, direction)

    const toOptions = (fields) => fields.map((field) => ({ value: field.name, label: field.name }))

    const changeSide = (side, fields, value) => {
        const field = fields.find((entry) => entry.name === value)
        // An unknown value means a field the source system no longer returns; keep
        // the name and leave the rest of that side's metadata untouched.
        onChange(row.rowId, withSideField(row, direction, side, field || {
            name: value,
            dataType: side === 'left' ? left.dataType : right.dataType,
            isRequired: side === 'left' ? left.isRequired : right.isRequired,
            size: side === 'left' ? left.size : right.size,
            type: side === 'left' ? left.type : right.type
        }))
    }

    return (
        <li className={[Shared.row, row.isNew ? Shared.rowNew : ''].join(' ').trim()}>
            <div className={`${Shared.grid} ${Style.grid}`}>
                <div className={Style.fieldCell}>
                    <MappingSelect
                        inputId={`productLeftAttribute-${row.rowId}`}
                        options={toOptions(leftFields)}
                        value={left.name}
                        placeholder={`Select ${leftTitle} Field`}
                        customLabel=""
                        customPlaceholder=""
                        hasError={left.name === ''}
                        isDisabled={isDisabled || row.default}
                        onChange={(value) => changeSide('left', leftFields, value)}
                    />
                    <span className={Style.fieldMeta}>
                        {left.dataType}
                        <RequiredFlag isRequired={left.isRequired} />
                    </span>
                </div>
                <span className={Shared.arrow} aria-hidden="true">&rarr;</span>
                <div className={Style.fieldCell}>
                    <MappingSelect
                        inputId={`productRightAttribute-${row.rowId}`}
                        options={toOptions(rightFields)}
                        value={right.name}
                        placeholder={`Select ${rightTitle} Field`}
                        customLabel=""
                        customPlaceholder=""
                        hasError={right.name === ''}
                        isDisabled={isDisabled || row.default}
                        onChange={(value) => changeSide('right', rightFields, value)}
                    />
                    <span className={Style.fieldMeta}>
                        {right.dataType}
                        <RequiredFlag isRequired={right.isRequired} />
                    </span>
                </div>
                {row.default ? (
                    <span className={Style.defaultBadge} title="Default mappings cannot be edited or removed">
                        Default
                    </span>
                ) : (
                    <button
                        type="button"
                        className={Shared.deleteButton}
                        title="Remove mapping"
                        aria-label={`Remove mapping for ${left.name || 'this row'}`}
                        disabled={isDisabled}
                        onClick={() => onRemove(row.rowId)}
                    >
                        <i className="uil-trash-alt" aria-hidden="true" />
                    </button>
                )}
            </div>
            {row.isNew ? <span className={Shared.newBadge}>Not saved yet</span> : null}
        </li>
    )
}

export default React.memo(AttributeMappingRow)
